import { useSyncExternalStore } from "react";
import type { KnowledgeBaseSearchResults } from "@/lib/knowledgeBaseSearch";

const EMPTY_RESULTS: KnowledgeBaseSearchResults = {
  tools: [],
  faq: [],
  personas: [],
  blog: [],
  topResult: null,
};

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 260;
const MAX_WIDTH = 480;
const WIDTH_STORAGE_KEY = "fdp-search-panel-width";

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    if (Number.isFinite(parsed)) return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
  } catch {
    /* ignore -- private browsing, storage disabled, etc. */
  }
  return DEFAULT_WIDTH;
}

/**
 * A desktop-only, docked (not modal) search panel -- inspired directly
 * by Copilot's own side-panel pattern in Microsoft's products: opens
 * only once someone actually starts typing (not on focus/click alone),
 * stays open until explicitly closed rather than auto-dismissing, and
 * is user-resizable via a drag handle on its left edge, with the
 * chosen width remembered across visits.
 *
 * Deliberately desktop-only -- mobile keeps its existing single-column
 * dropdown list unchanged, matching how this was actually approved:
 * the two-pane/docked treatment doesn't translate to a small screen,
 * and forcing it there would be worse than the simple list already
 * there.
 *
 * Same lightweight useSyncExternalStore pattern as usePaywallDialog.ts
 * and useUpdateBanner.ts, so the header search bar (present on every
 * page) and the homepage hero search can both feed into one shared
 * panel instance instead of each needing their own copy of this UI.
 *
 * query/results live here (not in the panel component itself) since
 * whichever input the user is actively typing into needs to push
 * updates in, and the panel doesn't otherwise know which one that is.
 */
interface SearchPanelState {
  isOpen: boolean;
  query: string;
  results: KnowledgeBaseSearchResults;
  /** The exact query `results` belong to -- distinct from `query`, which
   * is whatever is currently typed. Search now runs only on Enter, so
   * these can legitimately differ (someone edits the text after seeing
   * results); anything derived from "what was searched" (the multi-step
   * suggestion, the no-matches message) must read this, not `query`. */
  submittedQuery: string;
  isSearching: boolean;
  width: number;
}

let state: SearchPanelState = {
  isOpen: false,
  query: "",
  results: EMPTY_RESULTS,
  submittedQuery: "",
  isSearching: false,
  width: DEFAULT_WIDTH,
};

const listeners = new Set<() => void>();

function notify() {
  Array.from(listeners).forEach((l) => l());
}

function setState(partial: Partial<SearchPanelState>) {
  state = { ...state, ...partial };
  notify();
}

/** Called from a search input's onChange -- opens the panel the first
 * time real text appears (2+ chars, matching the existing knowledge-base
 * search's own minimum query length elsewhere in the app), and keeps
 * it open and updated on every subsequent keystroke. Does NOT reopen a
 * closed panel just because focus returns to the input with existing
 * text still in it -- only fresh typing (or the trigger explicitly
 * choosing to) opens it, matching "opens only when someone types
 * something," not on every refocus. */
export function updateSearchPanelQuery(query: string, results: KnowledgeBaseSearchResults) {
  const trimmed = query.trim();
  if (trimmed.length < 2) {
    setState({ query, results: EMPTY_RESULTS, submittedQuery: "", isSearching: false });
    return;
  }
  setState({ query, results, submittedQuery: trimmed, isSearching: false, isOpen: true });
}

// One token shared by every search surface (header, hero, panel). A
// search only gets to publish its results if no newer search has begun
// anywhere since -- so a slow AI-assisted result from one surface can
// never land on top of a newer search from another.
let searchToken = 0;

/** Called when someone submits a search (Enter). Publishes the query
 * text immediately and opens the panel in a "searching" state; returns
 * the token the caller must present to finishSearchPanelSearch. */
export function beginSearchPanelSearch(query: string): number {
  const token = ++searchToken;
  setState({ query, isSearching: true, isOpen: true });
  return token;
}

/** Publishes results for a finished search -- only if it's still the
 * latest one. Deliberately never writes `query`: results can arrive
 * seconds later (the AI fallback), by which time the person may have
 * typed more, and writing the old text back over it is exactly the
 * "search won't let me type" bug this replaced. Returns whether the
 * results were accepted. */
export function finishSearchPanelSearch(token: number, query: string, results: KnowledgeBaseSearchResults): boolean {
  if (token !== searchToken) return false;
  setState({ results, submittedQuery: query, isSearching: false, isOpen: true });
  return true;
}

export function failSearchPanelSearch(token: number) {
  if (token === searchToken) setState({ isSearching: false });
}

/** Invalidates any in-flight search (e.g. the text was cleared). */
export function cancelSearchPanelSearch() {
  searchToken++;
  setState({ isSearching: false });
}

/** Updates just the query text, synchronously, with no results change.
 * Exists specifically for SearchSidePanel's own <input>, which reads
 * `query` directly from this store to render its value -- unlike the
 * header/hero/palette inputs (each has its own separate, already-
 * synchronous local query state, and only feeds this store's `results`
 * after their own debounce), this panel's input has no other place to
 * keep the visible text, so debouncing the call that also updates
 * `results` would visibly lag the panel's own input behind every
 * keystroke -- exactly what debouncing search execution must never do
 * to the text actually shown in a box. */
export function setSearchPanelQueryText(query: string) {
  setState({ query });
}

export function closeSearchPanel() {
  setState({ isOpen: false });
}

export function setSearchPanelWidth(width: number) {
  const clamped = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
  setState({ width: clamped });
  try {
    localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
  } catch {
    /* ignore */
  }
}

export function useSearchPanelState() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => state
  );
}

// Hydrate the persisted width once, on module load (not inside a React
// effect, since the very first render should already reflect it rather
// than flashing the default width for one frame first).
state = { ...state, width: readStoredWidth() };

export const SEARCH_PANEL_MIN_WIDTH = MIN_WIDTH;
export const SEARCH_PANEL_MAX_WIDTH = MAX_WIDTH;
